import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  OtherFundDetailsDisplayComponent
} from "@products/other-details/other-fund-details-display-component.interface";
import { OtherFundDetailsComponentConfig } from "@products/other-details/other-fund-details-component.config";
import { Subscription, combineLatest } from 'rxjs';
import { Logger } from '@utils/logger';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import { Page, Component as BrComponent } from '@bloomreach/spa-sdk';
import {
  FundHeaderState,
  FundInformationAdditionalInfo,
  FundInformationOverview,
  FundInformationState,
  FundMangerList,
  ProductDetailConfiguration,
} from '@types';
import { FundInformationService } from '@components/products/overview/services/fund-information.service';
import { FundDocument } from '@components/products/types/fund-document.type';
import { FundHeaderService } from '@components/products/overview/services/fund-header.service';
import { DomSanitizer } from '@angular/platform-browser';
import FundPortfolioManagerQuery from '@graphql/overview/fund-portfolioManagers.graphql';
import moment from 'moment';
import { FundCompareService } from '@components/products/fund-listing/services/fund-compare.service';
import { FundPortfolioManagersService } from '@components/products/services/fund-portfolio-managers.service';
import { CommonService } from '@services/common.service';
import { TranslateService } from '@components/shared/translate/translate.service';

const logger = Logger.getLogger('FundMangersComponent');

@Component({
  selector: 'ft-fund-managers',
  templateUrl: './fund-managers.component.html',
  styleUrls: ['./fund-managers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundMangersComponent
  extends BaseProductDetailComponent
  implements OtherFundDetailsDisplayComponent, OnDestroy, OnInit {
  // Class variables.
  dataSubscription: Subscription;
  overview: FundInformationOverview;
  additionalInfo: FundInformationAdditionalInfo;
  isLoaded: boolean;
  fundManagers: FundMangerList[];
  fundDocument: FundDocument;
  isMobileView: boolean = false;
  fundManagerName: any;
  minimalInvestment: string;
  additionalInvestment: string;
  slideConfig = {
    dots: false,
    arrows: true,
    centerMode: false,
    rtl: false,
    prevArrow: "<button type='button' class='btn btn-outline-secondary left-arrow'><i class='bi bi-arrow-left-short'></i></button>",
    nextArrow: "<button type='button' class='btn btn-outline-secondary right-arrow'><i class='bi-arrow-right-short'></i></button>",
    // autoplay: true,
    infinite: false,
    autoplaySpeed: 3000,
    slidesToShow: 3,
    slidesToScroll: 1,
    swipeToSlide: true,
    touchThreshold: 100,
    variableWidth: true,
    responsive: [
      {
        breakpoint: 1300,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 2.2,
          slidesToScroll: 1
        }
      }
      ,
      {
        breakpoint: 600,

        settings: {
          arrows: false,
          slidesToShow: 1.2,
          slidesToScroll: 1
        }
      }
      ,
      {
        breakpoint: 576,

        settings: {
          autoplay: false,
          arrows: false,
          swipeToSlide: false,
          touchThreshold: false,
        }
      }
    ]
  };

  showTE: boolean;
  showTD: boolean;
  showLaunchData: boolean;
  TEData = [];
  TDData = [];
  LaunchData = [];
  TEAsofDate: string;
  TDAsofDate: string;
  shareClassArr = [''];
  fundsList: any = [];
  badgeValue: string;
  focusFunds: any = [];
  showFinacial: boolean = true;

  constructor(
    private fundInformationService: FundInformationService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private fundHeaderService: FundHeaderService,
    private fundPortfolioManagerService: FundPortfolioManagersService,
    private commonService: CommonService,
    private translateService: TranslateService,
    private fundCompareService: FundCompareService) {
    super();
  }

  ngOnInit(): void {
    if (window.innerWidth < 700) {
      this.isMobileView = true;
    }
    this.badgeValue = this.translateService.instant("products.focused-funds-badge");
    let focusFunds = this.translateService.instant("products.focused-funds-config");
    const focusFundsArray = focusFunds?.split(",");
    this.focusFunds = focusFundsArray?.map(number => {
      return {"fundId": number};
    });
  }

  populate(
    componentConfig: OtherFundDetailsComponentConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ): void {
    this.initialize(brConfig, component, page);
    const documents = component.getModels()?.documents;

    this.fundDocument = page.getContent(documents)?.getData<FundDocument>();
    logger.debug(page, this.page);

    if (this.brConfig.fundId) {
      this.dataSubscription = combineLatest([
        this.fundHeaderService.getFundHeaderState$(),
        this.fundInformationService.getFundInformationState$(),
        // this.fundCompareService.getFundCompareState$(),
      ]).subscribe(
        (data: any) => {
          let fundInformationState: FundInformationState;
          let fhState: FundHeaderState;
          // let fundcompare: FundCompareState;
          [fhState, fundInformationState] = data;
          this.isLoaded = fundInformationState.isLoaded;
          this.minimalInvestment = fhState.minimumInvestment;
          this.additionalInvestment = fhState.additionalInvestment;
          this.checkForTeTdData(fundInformationState.nav?.shareClasses);

          if (this.isLoaded) {
            this.overview = fundInformationState.overview;
            this.additionalInfo = fundInformationState.additionalInfo;
            this.fundManagers = this.additionalInfo?.fundManagers;
            this.showFinancialRatio(this.additionalInfo);
          }

          this.cdr.detectChanges();
        },
        (error) => {
          logger.error(
            `Error in invoking fund information service: ${error}`
          );

          this.isLoaded = false;
          this.cdr.detectChanges();
        }
      );
    }
  }

  showFinancialRatio(additionalInfo) {
    if (additionalInfo?.standardDeviation === undefined &&
      additionalInfo?.beta === undefined &&
      additionalInfo?.sharpeRatio === undefined &&
      additionalInfo?.rSquaredRatio === undefined &&
      additionalInfo?.riskFreeRate === undefined &&
      additionalInfo?.portfolioTurnover === undefined) {
      this.showFinacial = false;
    }
  }

  getImageForCoursel(ImageCoursel: string) {
    let imageName = ImageCoursel.split(' ').join('');
    const mgrImage = this.translateService.instant('products.fund-manager-image-' + imageName);
    return this.sanitizer.bypassSecurityTrustUrl(mgrImage);// NOSONAR
  }

  private checkForTeTdData(shareClasses) {
    if (shareClasses.length) {
      // TE data
      shareClasses.forEach((shareClass) => {
        if (
          shareClass.icradataservice &&
          shareClass.icradataservice.teasofdate
        ) {
          this.TEData.push({
            name: shareClass.name,
            te1year: shareClass.icradataservice.te1year,
          });
          this.showTE = true;
          this.TEAsofDate = moment(
            shareClass.icradataservice.teasofdate
          ).format('DD/MM/YYYY');
        }
        if (
          shareClass.icradataservice &&
          shareClass.icradataservice.tdasofdate
        ) {
          this.shareClassArr.push(shareClass.name);
          this.TDAsofDate = moment(
            shareClass.icradataservice.tdasofdate
          ).format('DD/MM/YYYY');
          this.showTD = true;
        }
      });

      // TD data
      if (this.showTD) {
        let rowArr = [];
        [
          {
            text: '1-Year',
            key: 'td1year',
          },
          {
            text: '3-Year',
            key: 'td3year',
          },
          {
            text: '5-Year',
            key: 'td5year',
          },
          {
            text: '10-Year',
            key: 'td10year',
          },
          {
            text: 'Since Launch',
            key: 'tdsiyear',
          },
        ].forEach((obj) => {
          rowArr = [];
          rowArr.push(obj.text);
          shareClasses.forEach((shareClass) => {
            if (shareClass.icradataservice) {
              rowArr.push(shareClass.icradataservice[obj.key]);
            }
          });
          this.TDData.push(rowArr);
        });
      }

      // Launch Date data
      shareClasses.forEach((shareClass) => {
        if (shareClass.icradataservice) {
          this.LaunchData.push({
            name: shareClass.icradataservice.schemename,
            date: moment(shareClass.inceptionDateStd).format('DD-MMM-YY'),
          });
          this.showLaunchData = true;
        }
      });
    }
  }

  public replaceNSEwithRegular(str) {
    return (str && str.replace('NSE', 'Regular')) || str;
  }

  viewManagerFunds(param: any) {
    this.fundsList = [];
    this.fundManagerName = param.name;
    this.fundPortfolioManagerService.register(FundPortfolioManagerQuery, {
      empId: param.empid,
    }).subscribe(data => {
      const fundIds = (data as any[]).map(item => item.fundid);
      this.loadfunds(fundIds);
    });
  }

  loadfunds(fundsid) {
    this.fundCompareService.loadfunds(fundsid);
    this.fundCompareService.getFundManagerState$().subscribe(data => {
      this.fundsList = data;
      for (let i = 0; i < this.fundsList.length; i++) {
        let selectedFund = this.fundsList[i];
        let selectedDropdown = selectedFund.performancePeriodDropdownItems.find(item => item.value === "averageAnnualReturnSinceInception");
        selectedFund.fundAge = this.fundAge(selectedFund.fundInceptionDate);
        selectedFund.selectedDropdown = selectedDropdown;
      }
      // Adding the Focus Badge.
      this.fundsList = this.fundsList.map(fund => {
        if (this.focusFunds.some(focusFund => focusFund.fundId === fund.fundId)) {
          return {...fund, badge: this.badgeValue ? this.badgeValue : 'focus'};
        }
        return fund;
      });
      this.fundsList.sort((a, b) => {
        // Check if funds have the badge: focus property
        const aHasFocusBadge = a.badge === "focus";
        const bHasFocusBadge = b.badge === "focus";
        if (aHasFocusBadge && !bHasFocusBadge) {
          return -1;
        } else if (!aHasFocusBadge && bHasFocusBadge) {
          return 1;
        }
        return 0;
      });
      this.cdr.detectChanges();
    });
  }

  updateSelectedDropdown(dropdown: any, fundId): void {
    let selectedFund = this.fundsList.find(fund => fund.fundId === fundId)
    selectedFund.selectedDropdown = dropdown;
  }

  public getEndOfLastQuarter(): string {
    const quarterAdjustment = (moment().month() % 3) + 1;
    const lastQuarterEndDate = moment()
      .subtract({months: quarterAdjustment})
      .endOf("month");

    return lastQuarterEndDate.format("L");
  }

  getRisk(productLabel?: string) {
    if (productLabel) return this.commonService.getRisk(productLabel);
  }

  private fundAge(date: any): number {
    const [month, day, year] = date.split('/');
    const today = new Date();
    let age = today.getFullYear() - year;
    const m = today.getMonth() - month;

    if (m < 0 || (m === 0 && today.getDate() < day)) {
      age--;
    }
    return age;
  }

  removeFundString(value: string) {
    if (value) {
      value = value?.replace(/funds|fund/gi, "");
      return value;
    }
    return '';
  }

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe();
  }

  protected readonly Number = Number;
}
